import { createContext, Dispatch, SetStateAction, useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { parseDate, getLocalTimeZone, today as createToday } from '@internationalized/date'
import { useTranslation } from 'react-i18next'

import { useLocalState } from 'src/settings/localStorage'
import DateRangePicker from 'src/components/input/DateRangePicker'
import clsx from 'clsx'
// import { eliminateDoubleMaintenanceActions, useAugmentMaintenanceAction } from "./utils";
import _ from 'lodash'
// import { StatusView } from "./MaintenanceActionInstruction";
import { memberUrl } from 'src/patents/utils'
import { Link } from 'src/navigation'
import { SortButton } from 'src/patents/datawizard/DataWizard'
// import { AugmentedMaintenanceAction } from "./utils";
import { IconSpinner } from 'src/components/icons'
import { useHints } from '../components/Hints'
import RenewalsProvider from '../interfaces/RenewalsProvider'
import { useSettings } from '../interfaces/Settings'
import { useAugmentRenewal, useRenewals } from '../interfaces/Renwals'
import { AugmentedRenewal, Renewal } from '../interfaces/renewal_types'
import { eliminateDoubleRenewals } from '../utils/renewals'
import { StatusView } from '../components/InstructionOverview'

export function RenewalsHistory() {
  const { t } = useTranslation()

  const { hint, isHintNeeded } = useHints()

  return (
    <>
      {/* @ts-ignore */}
      <Helmet>
        <title>
          {t('history')} | {t('renewals')} | Patent Cockpit
        </title>
      </Helmet>

      <RenewalHistoryProvider>
        <div className="header-row pb-1 flex flex-row gap-1 items-center max-w-4xl pr-2">
          <h2 className="modern-h2 mr-auto">{t('history')}</h2>
          <RenewalHistoryFilter />
        </div>
        <div className="px-2 pb-6 max-w-4xl">{isHintNeeded ? hint : <RenewalHistoryContext />}</div>
      </RenewalHistoryProvider>
    </>
  )
}

const today = createToday(getLocalTimeZone())

const sortByValues = ['internalReference', 'due', 'status']

const HistoryFilterContext = createContext({
  minDate: undefined as string,
  setMinDate: (() => {}) as Dispatch<SetStateAction<string>>,
  maxDate: undefined as string,
  setMaxDate: (() => {}) as Dispatch<SetStateAction<string>>,
  sortBy: sortByValues[0],
  setSortBy: (() => {}) as Dispatch<SetStateAction<string>>,
  sortOrder: 1 as number,
  setSortOrder: (() => {}) as Dispatch<SetStateAction<number>>,
})

function RenewalHistoryProvider({ children }) {
  const [sortBy, setSortBy] = useLocalState('renewals-sorty-by', sortByValues[0])
  const [sortOrder, setSortOrder] = useLocalState('renewals-sorty-order', -1)

  const [minDate, setMinDate] = useLocalState('renewals-history-min-date', today.add({ months: -6 }).toString())
  const [maxDate, setMaxDate] = useLocalState('renewals-history-max-date', today.toString())

  const value = { sortBy, setSortBy, sortOrder, setSortOrder, minDate, setMinDate, maxDate, setMaxDate }

  return <HistoryFilterContext.Provider {...{ value }}>{children}</HistoryFilterContext.Provider>
}

function useRenewalHistoryFilter() {
  return useContext(HistoryFilterContext)
}

function RenewalHistoryFilter() {
  const { t } = useTranslation()

  const { startDate } = useSettings()
  const { minDate, setMinDate, maxDate, setMaxDate } = useRenewalHistoryFilter()

  return (
    <DateRangePicker
      name="date range maintenances"
      className={clsx('text-sm py-1 px-2 bg-transparent w-fit')}
      title={t('instruction-due-within')}
      errorPosition="bottom-right"
      {...{ minDate, maxDate, setMinDate, setMaxDate, minValue: startDate ? parseDate(startDate) : undefined }}
    />
  )
}

function RenewalHistoryContext() {
  const { minDate, maxDate } = useRenewalHistoryFilter()
  return (
    <RenewalsProvider {...{ start: minDate, end: maxDate, states: ['Instructed', 'Paid', 'Cancelled', 'Open'] }}>
      <RenewalHistoryTable />
    </RenewalsProvider>
  )
}

function RenewalHistoryTable() {
  const { t } = useTranslation()
  const { sortBy, setSortBy, sortOrder, setSortOrder } = useRenewalHistoryFilter()

  const { augmentRenewal } = useAugmentRenewal()

  const { renewals: originalRenewals, isLoading } = useRenewals()

  const sortFunction 
    = sortBy === 'due'
    ? (m: Renewal) => m.instructionDueDate
    : sortBy === 'status'
    ? (m: Renewal) => m.state
    : (m: AugmentedRenewal) => m.member.internalReference

  const renewals = _(originalRenewals)
    .filter(Boolean)
    .map(augmentRenewal)
    .filter(m => m.member !== undefined)
    .sortBy(sortFunction)
    .tap(ms => (sortOrder > 0 ? ms : _.reverse(ms)))
    .value()

  // console.log({ originalRenewals: originalRenewals.filter(r => r.ipRightId !== undefined).length, renewals: renewals.length })
  const headers = [
    {
      key: 'patent',
      label: t('patent'),
      button: <SortButton {...{ searchField: 'internalReference', sortField: sortBy, sortOrder, setSortField: setSortBy, setSortOrder }} />,
    },
    {
      key: 'due',
      label: t('instruction-due'),
      button: <SortButton {...{ searchField: 'due', sortField: sortBy, sortOrder, setSortField: setSortBy, setSortOrder }} />,
    },
    {
      key: 'status',
      label: t('status'),
      button: <SortButton {...{ searchField: 'status', sortField: sortBy, sortOrder, setSortField: setSortBy, setSortOrder }} />,
    },
  ]

  const activeRenewals = new Set(
    _(renewals)
      .groupBy(({ member }) => member.familyMemberId)
      .values()
      .flatMap(ms => _.values(eliminateDoubleRenewals(ms)).map(m => m.id))
      .value(),
  )
  // console.log({maintenanceActions})

  return (
    <div className="grid grid-cols-[auto_auto_auto] gap-x-4 gap-y-2">
      <div className="grid grid-cols-subgrid col-span-3 border-b-2 border-pcx-300">
        {headers.map((h, i) =>
          h.key ? (
            <div
              key={h.key ?? i}
              className={clsx('py-2 xpx-3 text-sm text-pcx-600 font-semibold uppercase tracking-wider', 'first:pl-4 last:pr-4')}
            >
              {h.label} {h.button}
            </div>
          ) : (
            <div key={i} />
          ),
        )}
      </div>
      <div className="grid grid-cols-subgrid col-span-3 gap-y-1 py-3 px-2 bg-white rounded-xl shadow-lg border border-pcx-200">
        {isLoading && (
          <div className="col-span-3 text-xl text-pcx-500">
            <IconSpinner className="size-5 inline mr-2 animate-spin" />
            {t('loading')}...
          </div>
        )}
        {renewals.map(renewal => {
          const { member, status, instruction } = renewal
          const deprecated = !activeRenewals.has(renewal.id)
          return (
            <div className="grid grid-cols-subgrid col-span-3 px-2 py-0.5 hover:bg-pcx-200 rounded-md" key={renewal.id}>
              <div>
                <Link className="underline-link" to={memberUrl(member)}>
                  {renewal.member.internalReference}
                </Link>
              </div>
              <div>{renewal.instructionDueDate}</div>
              <div className="self-center">
                <StatusView {...{ renewal: renewal, status, instruction, deprecated }} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}